import React, { useEffect } from 'react';
import { Text, View } from 'react-native';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import { saveTask } from '@/data/tasks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import SelectInput from '@/components/General/inputs/SelectInput';
import Toast from 'react-native-toast-message'
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import {useEditScreenStore} from '@/data/store/useEditScreenStore';
import {defaultRedirects, redirectToDefault} from '@/utils/constants/linking';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import Toggle from '@/components/General/inputs/Toggle';
import { StyleSheet } from 'react-native-unistyles';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { areStringsPopulated } from '@/utils/InputValidator';
import { isAutomationDueType, isFollowUpDueType } from '@/utils/constants/taskTypes';


const taskTypeOptions = {
    "EVENT_VOLUNTEER": "Event Volunteer",
    "EVENT_SERVICE_FLOW": "Event Service Flow",
    "INDIVIDUAL": "Individual",
    "PATHS": "Automations",
    "AUTOMATIONS": "Automations",
    "FOLLOW_UP": "Follow Up",
}

// FOLLOW_UP is display-only here: care tasks are created atomically with
// their person context from the Follow Up surface (spec §10.2), never from
// the generic task form.
const creatableTaskTypeOptions = Object.keys(taskTypeOptions)
    .filter(key => !isFollowUpDueType(key))
    .map(key => taskTypeOptions[key]);

const EMPTY_DRAFT = {};

export default function AddTaskModal() {
    const { id, fromScreen, fromStoreKey, dueType: presetDueType } = useLocalSearchParams()
    const storeKey = id || "new"
    
    /*** State ***/
    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const taskDetails = useEditScreenStore(s => s.AddTaskModal?.[storeKey]) || EMPTY_DRAFT

    /*** Theme & Dimensions ***/
    const router = useRouter();

    /*** Queries ***/
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: taskToEdit } = Query.tasks.byId({id: id, options: {enabled: !!id}})

    useEffect(() => {
        if(taskToEdit && !Object.keys(taskDetails).length){
            const editData = {...taskToEdit}
            if(editData.dueType === 'INDIVIDUAL' && Array.isArray(editData.TaskInstances)){
                editData.TaskInstance = editData.TaskInstances[0] || editData.TaskInstance
            }
            // Extract all volunteer teams for multi-team selector
            editData.VolunteerTeams = editData.VolunteerTeamTasks?.map(vtt => vtt.VolunteerTeam).filter(Boolean) || [];
            setEditScreenData("AddTaskModal", storeKey, {
                ...editData,
                originalValues: editData,
            })
        }
    }, [taskToEdit])

    // Pre-set dueType when creating from another screen (e.g. path step task picker)
    useEffect(() => {
        if(presetDueType && !id && !taskDetails?.dueType && !isFollowUpDueType(presetDueType)){
            const updates = { dueType: presetDueType };
            if(presetDueType !== 'INDIVIDUAL' && !isAutomationDueType(presetDueType)) updates.assumeCompleted = true;
            setEditScreenData("AddTaskModal", storeKey, updates)
        }
    }, [presetDueType, id])

    const setTaskDetails = (details) => {
        setEditScreenData("AddTaskModal", storeKey, details)
    }

    const showAddToFutureEvents = taskDetails?.dueType === "EVENT_VOLUNTEER" && taskDetails?.VolunteerTeams?.length > 0

    const saveTaskMutation = useMutation({
        mutationFn: saveTask,
        onSuccess: async (data) => {
            removeEditScreenData("AddTaskModal", storeKey)
            queryClient.invalidateQueries({queryKey: QueryKeys.tasks.all()});

            if(taskDetails?.dueType == "EVENT_VOLUNTEER"){
                queryClient.invalidateQueries({queryKey: QueryKeys.events.all()});
                queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.all()});
            }

            if(taskDetails?.dueType == "INDIVIDUAL"){
                queryClient.invalidateQueries({queryKey: QueryKeys.taskInstances.individual()});
            }

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddTaskModal")
            }
            
        },
        onError: (err) => {
            console.debug(err)
        },
    })


    
    return (
        <ModalWrapper
            title={id ? "Update Task" : "New Task"}
            loading={saveTaskMutation.isPending} 
            previousScreen={defaultRedirects.AddTaskModal} 
            onCancel={() => {
                removeEditScreenData("AddTaskModal", storeKey)
            }} 
            onConfirm={() => {
                if(!areStringsPopulated([taskDetails?.title])){
                    Toast.show({ type: 'error', text2: `Tasks must have a title and description!` });
                    return true;
                }

                if(!taskDetails?.dueType){
                    Toast.show({ type: 'error', text2: `Tasks must have a Task Type!` });
                    return true;
                }

                if(isFollowUpDueType(taskDetails?.dueType) || isFollowUpDueType(taskDetails?.originalValues?.dueType)){
                    Toast.show({ type: 'error', text2: `Follow Up tasks are created and managed from a person's Follow Up page.` });
                    return true;
                }

                if(taskDetails?.dueType === "INDIVIDUAL" && !taskDetails?.TaskInstance?.dueDate){
                    Toast.show({ type: 'error', text2: `Individual tasks must have a due date!` });
                    return true;
                }

                const details = {...taskDetails}

                const { originalValues, ...cleanDetails } = details;
                saveTaskMutation.mutate({details: cleanDetails, originalValues, currentUser: loggedInUser?.id})
            }} 
        >
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <InputSection title="Task Details" description="Provide the basic information for your new task.">
                    <Input
                        title="Title"
                        required
                        value={taskDetails?.title}
                        maxLength={120}
                        onUpdate={(new_value) => {
                            setTaskDetails({
                                title: new_value
                            })
                        }}
                    />

                    <Input
                        title="Description"
                        value={taskDetails?.description}
                        multiline
                        onUpdate={(new_value) => {
                            setTaskDetails({
                                description: new_value
                            })
                        }}
                    />

                    <SelectInput
                        title="Task Type"
                        required
                        disabled={!!id || !!presetDueType}
                        value={taskTypeOptions[taskDetails?.dueType]}
                        options={creatableTaskTypeOptions}
                        onUpdate={(selected) => {
                            const newDueType = Object.keys(taskTypeOptions).find(key => taskTypeOptions[key] == selected);
                            const updates = { dueType: newDueType };
                            if(newDueType !== 'INDIVIDUAL' && !isAutomationDueType(newDueType) && taskDetails?.assumeCompleted === undefined) updates.assumeCompleted = true;
                            setTaskDetails(updates);
                        }}
                    />

                    {taskDetails?.dueType === "EVENT_VOLUNTEER" && (
                        <SheetSelector addScreenName={"tasks/teams/create"} type="taskTeams" title="Teams" fromScreen={`tasks/create`} fromStoreKey={storeKey} multiple value={taskDetails?.VolunteerTeams || []} onChange={(selected_items) => {
                            setTaskDetails({
                                VolunteerTeams: selected_items
                            })
                        }} />
                    )}

                    {taskDetails?.dueType === "INDIVIDUAL" && (
                        <DatePickerInput
                            title="Due Date"
                            mode="datetime"
                            value={taskDetails?.TaskInstance?.dueDate}
                            onUpdate={(new_value) => {
                                setTaskDetails({
                                    TaskInstance: {
                                        ...taskDetails?.TaskInstance,
                                        dueDate: new_value
                                    }
                                })
                            }}
                        />
                    )}

                    {showAddToFutureEvents && (
                        <View style={styles.section}>
                            <View style={styles.sectionInner}>
                                <Text style={styles.mainText}>Add to future events on these teams</Text>
                                <Toggle
                                    onValueChange={() => setTaskDetails({
                                        addToFutureEvents: !taskDetails?.addToFutureEvents
                                    })}
                                    value={taskDetails?.addToFutureEvents || false}
                                />
                            </View>
                            <Text style={styles.secondaryText}>This will add this task to all upcoming event instances that already include these teams.</Text>
                        </View>
                    )}

                    {isAutomationDueType(taskDetails?.dueType) && (
                        <Text style={styles.secondaryText}>Assignment members are configured per path on the path step itself.</Text>
                    )}

                    {isFollowUpDueType(taskDetails?.dueType) && (
                        <Text style={styles.secondaryText}>Follow Up tasks carry a person&rsquo;s care context and are created from their Follow Up page.</Text>
                    )}

                </InputSection>

                <InputSection title="Priority" description="How important is this task when compared to other similar tasks?">
                    <ToggleInput
                        selectedValues={[taskDetails?.priority || "Standard"]}
                        items={["High", "Standard", "Low"]}
                        onItemChanged={(selected) => {
                            setTaskDetails({
                                priority: selected
                            });
                        }}
                    />
                </InputSection>

                <InputSection title="Task Options">
                    {taskDetails?.dueType !== "INDIVIDUAL" && !isAutomationDueType(taskDetails?.dueType) && !isFollowUpDueType(taskDetails?.dueType) && (
                        <View style={styles.section}>
                            <View style={styles.sectionInner}>
                                <Text style={styles.mainText}>Auto complete this task</Text>
                                <Toggle
                                    onValueChange={() => setTaskDetails({
                                        assumeCompleted: !taskDetails?.assumeCompleted
                                    })}
                                    value={taskDetails?.assumeCompleted || false}
                                />
                            </View>
                            <Text style={styles.secondaryText}>This will automatically mark the task as completed after the Due Date or Event is passed.</Text>
                        </View>
                    )}

                    <View style={styles.section}>
                        <View style={styles.sectionInner}>
                            <Text style={styles.mainText}>Allow Self Assignment</Text>
                            <Toggle 
                                onValueChange={() => setTaskDetails({
                                    allowSelfAssignment: !taskDetails?.allowSelfAssignment
                                })}
                                value={taskDetails?.allowSelfAssignment || false}
                            />
                        </View>
                        <Text style={styles.secondaryText}>This will allow people to assign themselves to this task. An &apos;Assign to me&apos; option will be added to the task.</Text>
                    </View>

                    <View style={styles.section}>
                        <View style={styles.sectionInner}>
                            <Text style={styles.mainText}>Allow Assignees to Forward</Text>
                            <Toggle 
                                onValueChange={() => setTaskDetails({
                                    allowForwarding: !taskDetails?.allowForwarding
                                })}
                                value={taskDetails?.allowForwarding || false}
                            />
                        </View>
                        <Text style={styles.secondaryText}>This will allow assignees to forward this task to someone else of their choosing in the case that they decline.</Text>
                    </View>
                </InputSection>
            </KeyboardAwareScrollView>
        </ModalWrapper> 
    );
}


const styles = StyleSheet.create(({colors}) => ({
    mainText: {
        color:colors.text, 
        fontSize:15
    },
    secondaryText: {
        fontStyle:"italic", 
        fontSize:12, 
        paddingTop:2, 
        color:colors.textSecondary
    },
    section: {
        paddingVertical:10,
        // borderTopWidth:1,
        // borderTopColor:colors.background
    },
    sectionInner: {
        flexDirection:"row", 
        justifyContent:'space-between', 
        alignItems:"center"
    }
}))